import type { ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import {
  Form,
  useActionData,
  useLoaderData,
  useSearchParams,
} from "@remix-run/react";
import { useEffect, useRef } from "react";
import { authenticateUser } from "~/models/auth.server";
import { createUserSession, getUserId } from "~/models/session.server";

export async function loader({ request }: LoaderFunctionArgs) {
  const userId = await getUserId(request);
  if (userId) return redirect("/");
  return json({
    adDomain: process.env.LDAP_AD_DOMAIN,
    bindDn: process.env.LDAP_BIND_DN,
    userIdProperty: process.env.LDAP_USER_ID_PROPERTY,
  });
}

export async function action({ request }: ActionFunctionArgs) {
  const defaultDomain = process.env.LDAP_AD_DOMAIN;

  const formData = await request.formData();
  const domain = formData.get("domain");
  const username = formData.get("username");
  const password = formData.get("password");
  const redirectTo = formData.get("redirectTo");
  const remember = formData.get("remember");

  if (
    !username ||
    !password ||
    typeof username !== "string" ||
    typeof password !== "string"
  ) {
    return json(
      { errors: { form: "Username and password are required" } },
      { status: 400 },
    );
  }

  if (!domain && defaultDomain) {
    return json({ errors: { form: "Domain is required" } }, { status: 400 });
  }

  if (domain && typeof domain !== "string") {
    return json(
      { errors: { form: "Domain must be a string" } },
      { status: 400 },
    );
  }

  try {
    const user = await authenticateUser({
      domain: domain ?? undefined,
      username,
      password,
    });
    if (!user) {
      return json({ errors: { form: "Invalid credentials" } }, { status: 401 });
    }
    return createUserSession({
      request,
      userId: user.id,
      remember: remember === "on",
      redirectTo: typeof redirectTo === "string" ? redirectTo : "/",
    });
  } catch (error) {
    console.error("Login error:", error);
    return json({ errors: { form: "Invalid credentials" } }, { status: 401 });
  }
}

export default function LoginPage() {
  const { adDomain, bindDn, userIdProperty } = useLoaderData<typeof loader>();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo") || "/";
  const actionData = useActionData<typeof action>();
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (actionData?.errors?.form) {
      usernameRef.current?.focus();
    }
  }, [actionData]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
        </div>
        <Form method="post" className="mt-8 space-y-6">
          <input type="hidden" name="redirectTo" value={redirectTo} />
          <div className="-space-y-px rounded-md shadow-sm">
            {adDomain ? (
              <div>
                <label htmlFor="domain" className="sr-only">
                  Domain
                </label>
                <input
                  id="domain"
                  name="domain"
                  type="text"
                  defaultValue={adDomain}
                  required
                  className="relative block w-full rounded-t-md border-0 px-2 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                  placeholder="Domain"
                />
              </div>
            ) : null}

            <label htmlFor="username" className="sr-only">
              Username
            </label>
            {bindDn && !adDomain ? (
              <div className="flex">
                <div className="px-2 py-1.5 bg-gray-50 border-0 ring-1 ring-inset ring-gray-300 rounded-md rounded-r-none">
                  {userIdProperty}=
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  ref={usernameRef}
                  required
                  className="relative block w-full border-0 px-2 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                  placeholder="Username"
                />
                <div className="px-2 py-1.5 bg-gray-50 border-0 ring-1 ring-inset ring-gray-300 rounded-md rounded-l-none">
                  {bindDn}
                </div>
              </div>
            ) : (
              <input
                id="username"
                name="username"
                type="text"
                ref={usernameRef}
                required
                className="relative block w-full border-0 px-2 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                placeholder="Username"
              />
            )}

            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                ref={passwordRef}
                required
                className="relative block w-full rounded-b-md border-0 px-2 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                placeholder="Password"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember"
                name="remember"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
              />
              <label
                htmlFor="remember"
                className="ml-2 block text-sm text-gray-900"
              >
                Remember me
              </label>
            </div>
          </div>

          {actionData?.errors?.form && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="text-sm text-red-700">
                  {actionData.errors.form}
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              className="group relative flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              Sign in
            </button>
          </div>
        </Form>
      </div>
    </div>
  );
}
